import { get } from 'js-cookie';
import { OPT_IN_REGIONS, OPT_OUT_REGIONS } from './utils/countries';
const ONETRUST_CONSENT_PROMPT_CLOSED_COOKIE = 'OptanonAlertBoxClosed';
const ONETRUST_CONSENT_CHANGED_EVENT = 'oneTrustConsentChangedEvent';
/**
 * Initially I wanted to avoid using the OptanonWrapper at all, but the OneTrust SDK
 * is not always initialized by the time we want to listen for consent changes. Therefore,
 * we're setting this up to fire an event that anyone can listen to.
 *
 * As an overview, OneTrust will call the global OptanonWrapper function when consent changes.
 * We will make use of this and dispatch our own consent changed event.
 */
globalThis.OptanonWrapper = function () {
    fireConsentChangedEvent();
};
let consentChangedListeners = [];
/**
 * The valid OneTrust categories for privacy
 */
export var OneTrustCategory;
(function (OneTrustCategory) {
    OneTrustCategory[OneTrustCategory["STRICTLY_NECESSARY"] = 0] = "STRICTLY_NECESSARY";
    OneTrustCategory[OneTrustCategory["PERFORMANCE"] = 1] = "PERFORMANCE";
    OneTrustCategory[OneTrustCategory["FUNCTIONAL"] = 2] = "FUNCTIONAL";
    OneTrustCategory[OneTrustCategory["TARGETING"] = 3] = "TARGETING";
})(OneTrustCategory || (OneTrustCategory = {}));
/**
 * A mapping from the friendly category enum to the underlying OneTrust category data found in OnetrustActiveGroups
 */
export const OneTrustCategoryGroupMapping = new Map([
    [OneTrustCategory.STRICTLY_NECESSARY, 'C0001'],
    [OneTrustCategory.PERFORMANCE, 'C0002'],
    [OneTrustCategory.FUNCTIONAL, 'C0003'],
    [OneTrustCategory.TARGETING, 'C0004'],
]);
/**
 * A helper function to determine if the category is consented.
 *
 * Note that if the OnetrustActiveGroups global is not found, it is assumed to be disabled
 * and therefore everything is consented
 * @param category The OneTrust category to be consented
 */
export function isOneTrustCategoryConsented(category) {
    // If absolutely necessary there is no reason to check with one trust.
    if (category === OneTrustCategory.STRICTLY_NECESSARY) {
        return true;
    }
    if (!isOneTrustActive()) {
        // Deny because we must wait for OneTrust to set itself up.
        if (getOneTrustDenyByDefault()) {
            return false;
        }
        else {
            return true;
        }
    }
    if (!OneTrustCategoryGroupMapping.has(category)) {
        throw new Error(`${category} is not a valid OneTrust category`);
    }
    return (OneTrustCategoryGroupMapping.has(category) &&
        OnetrustActiveGroups.includes(OneTrustCategoryGroupMapping.get(category)));
}
/**
 * A function that will fire the callback whenever the consent changes.
 * On initial load, this will be useful as certain properties like telemetry should be enabled on consent.
 * @param callback A callback to fire when consent changes
 */
export function onOneTrustConsentChanged(callback, options = {}) {
    function onOneTrustConsentChanged() {
        callback();
        if (options.once) {
            document.removeEventListener(ONETRUST_CONSENT_CHANGED_EVENT, onOneTrustConsentChanged);
        }
    }
    if (globalThis.document !== undefined) {
        document.addEventListener(ONETRUST_CONSENT_CHANGED_EVENT, onOneTrustConsentChanged);
        consentChangedListeners.push(onOneTrustConsentChanged);
    }
}
/**
 * Fires a callback if consent has already been given. Otherwise, it will wait for
 * the initial consent, and won't run afterwards.
 * @param category The category consented to
 * @param callback The callback to run on consent or immediately
 */
export function onInitialOneTrustConsent(category, callback) {
    // If consent is already given, run the callback immediately.
    if (isOneTrustCategoryConsented(category)) {
        callback();
    }
    else {
        onOneTrustConsentChanged(callback, { once: true });
    }
}
/**
 * Runs the callback immediately, and on every change.
 * @param callback The callback to run
 */
export function nowAndOnOneTrustConsentChanged(callback) {
    callback();
    onOneTrustConsentChanged(callback);
}
/**
 * A function that helps determine if the prompt has ever been dismissed, using the
 * OptanonAlertBoxClosed cookie: https://community.cookiepro.com/s/article/UUID-1e75fb0d-bb79-2af8-cb85-f905d16f1220
 */
export function isOneTrustPromptDismissed() {
    return Boolean(get(ONETRUST_CONSENT_PROMPT_CLOSED_COOKIE));
}
export function isOneTrustActive() {
    // A lack of this global means OneTrust is not running on the site
    return globalThis.OnetrustActiveGroups !== undefined;
}
/**
 * Toggles the Cookie Settings panel, using the OneTrust.ToggleInfoDisplay function
 */
export function showCookieSettings() {
    if (!isOneTrustActive() || !isOneTrustSDKAvailable()) {
        return;
    }
    OneTrust.ToggleInfoDisplay();
}
/**
 * Blocks the corresponding Google Analytics ID when the specified category is not consented to
 * @param gaId The google analytics ID to block
 * @param category The OneTrust category to consent to
 */
export function blockGoogleAnalytics(gaId, category) {
    if (!isOneTrustActive() || !isOneTrustSDKAvailable()) {
        return;
    }
    if (!OneTrustCategoryGroupMapping.has(category)) {
        throw new Error(`${category} is not a valid OneTrust category`);
    }
    OneTrust.BlockGoogleAnalytics(gaId, OneTrustCategoryGroupMapping.get(category));
}
function isOneTrustSDKAvailable() {
    return globalThis.OneTrust !== undefined;
}
/**
 * By default, deny by default is true.
 * If this is true, we will deny all categories by default. Therefore, if
 * OneTrust is not found on the page, then we will deny the category and
 * assume OneTrust must be instantiated on the page eventually,
 * but is not yet setup. If false, assume we allow by default if OneTrust
 * is not found on the page
 * @param value The value of the denyByDefault variable
 */
export function setOneTrustDenyByDefault(value) {
    if (globalThis.window) {
        globalThis.window.OneTrustDenyByDefault = value;
    }
}
export function getOneTrustDenyByDefault() {
    if (globalThis.window) {
        if (globalThis.window.OneTrustDenyByDefault === undefined) {
            globalThis.window.OneTrustDenyByDefault = true;
        }
        return globalThis.window.OneTrustDenyByDefault;
    }
    return false;
}
export function removeAllConsentChangedListeners() {
    if (!globalThis.document) {
        return;
    }
    for (const listener of consentChangedListeners) {
        document.removeEventListener(ONETRUST_CONSENT_CHANGED_EVENT, listener);
    }
    consentChangedListeners = [];
}
export function fireConsentChangedEvent() {
    if (globalThis.document) {
        document.dispatchEvent(new Event(ONETRUST_CONSENT_CHANGED_EVENT));
    }
}
/**
 * Our clues to show that the user should see settings are. If deny by default is enabled, then we should:
 * 1. They are in an opt-in region or
 * 2. The consent model is opt in, meaning they can see the banner or
 * 3. They are in an opt out region or
 * 4. The consent model is opt out.
 *
 * Note that the other types of consent models are Notify Only, custom, or none, which we will
 * not check for.
 * @returns True if we should show settings, false otherwise
 */
export function shouldShowSettings({ optOutEnabled, } = {}) {
    // Only check opt out if we enable it
    const isOptOut = optOutEnabled && (isOptOutConsentModel() || isOptOutRegion());
    return (getOneTrustDenyByDefault() &&
        (isOptOut || isOptInRegion() || isOptInConsentModel()));
}
/**
 * Determine if the consent rule is OptIn
 * @returns True if opt in, false otherwise
 */
export function isOptInConsentModel() {
    var _a, _b;
    if (!isOneTrustSDKAvailable() || !globalThis.OneTrust.GetDomainData) {
        return false;
    }
    return ((_b = (_a = globalThis.OneTrust.GetDomainData()) === null || _a === void 0 ? void 0 : _a.ConsentModel) === null || _b === void 0 ? void 0 : _b.Name) === 'opt-in';
}
/**
 * Determine if the consent rule is OptOut
 * @returns True if opt out, false otherwise
 */
export function isOptOutConsentModel() {
    var _a, _b;
    if (!isOneTrustSDKAvailable() || !globalThis.OneTrust.GetDomainData) {
        return false;
    }
    return ((_b = (_a = globalThis.OneTrust.GetDomainData()) === null || _a === void 0 ? void 0 : _a.ConsentModel) === null || _b === void 0 ? void 0 : _b.Name) === 'opt-out';
}
/**
 * This function should be removed in favor of SqOneTrust: https://github.com/squareup/sq-one-trust/pull/9
 * @returns True if located in an opt-out region, false otherwise
 */
export function isOptOutRegion() {
    var _a;
    if (!isOneTrustSDKAvailable() || !globalThis.OneTrust.getGeolocationData) {
        return false;
    }
    else {
        const country = ((_a = globalThis.OneTrust.getGeolocationData()) === null || _a === void 0 ? void 0 : _a.country) || '';
        return OPT_OUT_REGIONS.has(country);
    }
}
/**
 * @returns True if located in an opt-in region, such as the EU, false otherwise
 */
export function isOptInRegion() {
    var _a;
    if (!isOneTrustSDKAvailable() || !globalThis.OneTrust.getGeolocationData) {
        return false;
    }
    else {
        const country = ((_a = globalThis.OneTrust.getGeolocationData()) === null || _a === void 0 ? void 0 : _a.country) || '';
        return OPT_IN_REGIONS.has(country);
    }
}
/**
 * Returns the current OnetrustActiveGroups string
 * See: https://community.cookiepro.com/s/article/UUID-66bcaaf1-c7ca-5f32-6760-c75a1337c226
 */
export function getOneTrustActiveGroups() {
    if (!isOneTrustActive()) {
        return '';
    }
    return globalThis.OnetrustActiveGroups;
}
//# sourceMappingURL=onetrust-handler.js.map